import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Loader2, AlertCircle, FileText, Pencil, Trash2 } from 'lucide-react';
import { formsApi, appsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

export function FormsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Fetch forms from API
  const {
    data: formsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['forms'],
    queryFn: () => formsApi.list(),
  });

  // Fetch apps for the mapping
  const { data: appsData } = useQuery({
    queryKey: ['apps'],
    queryFn: () => appsApi.list(),
  });

  // Delete form mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => formsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forms'] });
      setDeleteId(null);
    },
  });

  const handleEdit = (formId: string) => {
    navigate(`/forms/${formId}`);
  };

  const forms = formsData?.items || [];
  const apps = appsData?.items || [];
  const appMap = new Map(apps.map((app) => [app.id, app.name]));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">Formulaires</h2>
          <p className="text-sm text-secondary-500">Créez et gérez vos formulaires</p>
        </div>
        <Button onClick={() => navigate('/forms/new')}>
          <Plus className="mr-2 h-4 w-4" />
          Nouveau Formulaire
        </Button>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
          <span className="ml-2 text-secondary-500">Chargement des formulaires...</span>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertCircle className="h-5 w-5" />
            <span>Erreur lors du chargement: {(error as Error).message}</span>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && forms.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-secondary-300 py-12 dark:border-secondary-700">
          <FileText className="h-12 w-12 text-secondary-400" />
          <h3 className="mt-4 text-lg font-medium text-secondary-900 dark:text-white">
            Aucun formulaire
          </h3>
          <p className="mt-2 text-sm text-secondary-500">
            Créez votre premier formulaire pour collecter des données.
          </p>
          <Button className="mt-4" onClick={() => navigate('/forms/new')}>
            <Plus className="mr-2 h-4 w-4" />
            Créer un formulaire
          </Button>
        </div>
      )}

      {/* Forms grid */}
      {!isLoading && !error && forms.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {forms.map((form) => (
            <div
              key={form.id}
              className="rounded-xl border border-secondary-200 bg-white p-5 dark:border-secondary-800 dark:bg-secondary-900"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30">
                    <FileText className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-secondary-900 dark:text-white">{form.name}</h3>
                    <p className="text-sm text-secondary-500">
                      {appMap.get(form.app_id) || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
              {form.description && (
                <p className="mt-3 text-sm text-secondary-500 line-clamp-2">{form.description}</p>
              )}
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs text-secondary-400">
                  {new Date(form.created_at).toLocaleDateString('fr-FR')}
                </span>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(form.id)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Dialog open={deleteId === form.id} onOpenChange={(open) => !open && setDeleteId(null)}>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteId(form.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Supprimer le formulaire</DialogTitle>
                        <DialogDescription>
                          Êtes-vous sûr de vouloir supprimer "{form.name}" ? Cette action est irréversible.
                        </DialogDescription>
                      </DialogHeader>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteId(null)}>
                          Annuler
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={() => deleteMutation.mutate(form.id)}
                          disabled={deleteMutation.isPending}
                        >
                          {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          Supprimer
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
